import { useCallback, useEffect, useRef } from "react";
import logoGray from "../../assets/logo-gray.svg";
import SectionTitle from "../../components/SectionTitle/SectionTitle";
import { useGetUserAnalytics } from "../../hooks/liquidium/Analytics/useGetUserAnalytics";
import { useUserStore } from "../../store";
import { useGetLeaderboard } from "../../hooks/liquidium/Analytics/useGetLeaderboard";
import LoadingBar from "../../components/Reusable/LoadingBar/Loading";
import { formatAddress } from "../../utils";

const Leaderboard = () => {
  const itemsPerPage = 10;
  const ordAddress = useUserStore((state) => state.ordAddress);
  const { singleValues, fetchUserAnalytics, loading, error } =
    useGetUserAnalytics();
  const {
    leaderboardData,
    fetchLeaderboard,
    loadingLeaderboard,
    leaderboardError,
    isLastLeaderboardPage,
  } = useGetLeaderboard();

  const observer = useRef<IntersectionObserver | null>(null);

  const lastLeaderboardElementRef = useCallback(
    (node) => {
      if (loadingLeaderboard) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          !isLastLeaderboardPage &&
          leaderboardData?.length
        ) {
          fetchLeaderboard(itemsPerPage, leaderboardData.length, false);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loadingLeaderboard, isLastLeaderboardPage],
  );

  useEffect(() => {
    if (ordAddress) {
      fetchUserAnalytics(ordAddress);
      fetchLeaderboard(itemsPerPage, 0, true);
      return;
    }
  }, [ordAddress]);

  return (
    <div className="flex justify-center relative w-full mb-10">
      <div className="flex flex-col  z-10  mt-12 xl:mt-7 w-screen max-w-[600px] gap-8">
        <SectionTitle
          title="Leaderboard"
          infoText={
            <>
              <p className="text-gray-400 font-semibold text-xs sm:text-sm mt-2">
                Points are rewarded for actively lendings and borrowing. You
                must have a score of at least 1 to be included in the
                leaderboard.
              </p>
            </>
          }
        />
        {ordAddress && (
          <>
            <div
              className={`p-3 rounded-lg border border-white/20 blurBackgroundXL ${
                loading ? "animate-pulse" : ""
              }`}
            >
              <div className="flex justify-between w-full">
                <p className="font-semibold text-lg sm:text-xl text-zinc-100">
                  Your Score
                </p>
                <a
                  href={"https://www.ord.io/" + ordAddress}
                  target="_blank"
                  className="px-2 py-[0] border border-white/20 rounded  hover:text-zinc-50 hover:border-white/40 font-medium sm:text-sm text-xs text-zinc-500 transition-all flex items-center  hover:bg-zinc-900  font-mono"
                >
                  {formatAddress(ordAddress)}
                </a>
              </div>
              <p className="font-extrabold text-3xl sm:text-4xl text-orange-400 mt-1">
                {loading ? (
                  <>--</>
                ) : singleValues?.totalPoints ? (
                  singleValues?.totalPoints
                ) : (
                  "0"
                )}
              </p>
              <p className="font-medium sm:text-sm text-xs text-zinc-500 mt-1">
                Ranked #{singleValues?.rank ? singleValues.rank : "0"}/
                {singleValues?.totalLeaderboardEntries}
              </p>
            </div>
            <div className="h-[1px] w-full bg-white/20"></div>
          </>
        )}
        {/* Leaderboard */}
        <div className="p-3 rounded-lg border border-white/20 blurBackgroundXL">
          <div className="flex justify-between w-full">
            <p className="font-semibold text-lg sm:text-xl text-zinc-100">
              Rankings
            </p>
          </div>
          {leaderboardData?.length && leaderboardData?.length > 0 ? (
            <>
              <div className="flex w-full max-h-[400px] overflow-y-scroll overflow-x-hidden custom-scrollbar flex-col mt-2">
                <table className="w-full text-left">
                  <thead className="top-0 sticky bg-black/[85%] rounded-md h-[40px] backdrop-blur-2xl">
                    <tr className="font-semibold text-xs sm:text-sm text-zinc-400 mb-2">
                      <th>Rank</th>
                      <th>Points</th>
                      <th className="sm:table-cell hidden">Completed Loans</th>
                      <th className=" ">Address</th>
                    </tr>
                  </thead>
                  <tbody className="font-normal text-xs sm:text-sm text-zinc-400">
                    {leaderboardData?.map((user, index) => {
                      const rank = index + 1;
                      const isLastElement =
                        leaderboardData.length === index + 1;
                      const isTopThree = rank == 1 || rank == 2 || rank == 3;
                      const rankClass = isTopThree ? "h-[50px]" : "h-[36px]";
                      const borderClass =
                        rank !== leaderboardData.length - 1
                          ? "border-b-[1px]"
                          : "";
                      const colorClass =
                        rank === 1
                          ? "text-orange-400 font-extrabold"
                          : rank === 2
                          ? "text-[#ec4731e1] font-extrabold"
                          : rank === 3
                          ? "text-[#cb2825d5] font-extrabold"
                          : "";
                      const rankIcon =
                        rank === 1
                          ? `‎ ‎ 🐐`
                          : rank === 2
                          ? `‎ ‎ 🐳`
                          : rank === 3
                          ? `‎ ‎ 🐟`
                          : "";

                      return (
                        <tr
                          ref={isLastElement ? lastLeaderboardElementRef : null}
                          className={`${borderClass} ${rankClass} border-zinc-800 table-row z-10`}
                        >
                          <td>
                            <p>
                              #{rank}
                              {rankIcon}
                            </p>
                          </td>
                          <td className={colorClass}>{user.points}</td>
                          <td className="sm:table-cell hidden">
                            {user.completedLoans}
                          </td>
                          <td className=" cursor-pointer	">
                            <a
                              href={"https://www.ord.io/" + user.userId}
                              target="_blank"
                              className="px-2 py-[0] border border-white/20 rounded  hover:text-zinc-50 hover:border-white/40 transition-all max-w-[100px] -mr-[100px] hover:bg-zinc-900  font-mono"
                            >
                              {formatAddress(user.userId)}
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {loadingLeaderboard && (
                  <div className=" w-full">
                    <LoadingBar white />
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {loadingLeaderboard && (
                <div className="my-5 w-full">
                  <LoadingBar />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <img
        src={logoGray}
        alt="background logo"
        width={300}
        height={300}
        className="absolute  sm:left-[0%] left-[-40%] w-[60vh] translate-y-[110px] z-0 opacity-10"
      />
    </div>
  );
};

export default Leaderboard;
